import { HotSpot } from '@xnav/types/src/common'
import { TimelineCardProps } from '../components/markupTimeline'
import { EditContent, EditHotSpot, EditPolygon } from '../pages/editor'

export function makeTimestampFromPixelOffset(xOffset: number, timelineWidth: number, videoDuration: number): number {
	return (videoDuration / timelineWidth) * xOffset
}

export function makePixelOffsetFromTimestamp(timestamp: number, timelineWidth: number, videoDuration: number): number {
	return (timelineWidth / videoDuration) * timestamp
}

export function applyDragToPolygon(polygon: EditPolygon, newStart: number, newEnd: number): EditPolygon {
	return {
		...polygon,
		start: newStart,
		end: newEnd
	}
}

export function applyDragToHotspot(hotspot: EditHotSpot, newStart: number, newEnd: number): EditHotSpot {
	// Fetch the hotspots indecies that are inside the new cropping range
	const newTimestampStart = hotspot.timeStamps.findIndex(timeStamp => timeStamp >= newStart)
	const newTimestampEnd = hotspot.timeStamps.findLastIndex(timeStamp => timeStamp <= newEnd) + 1
	// Create a new hotspot with only these indecies
	const newHotspot: HotSpot = {
		...hotspot,
		timeStamps: hotspot.timeStamps.slice(newTimestampStart, newTimestampEnd),
		points: hotspot.points.slice(newTimestampStart, newTimestampEnd)
	}
	// Update the hotspot to ensure we go up to our outer bounds
	if (newHotspot.timeStamps[0] !== newStart) {
		newHotspot.timeStamps.unshift(newStart)
		newHotspot.points.unshift(newHotspot.points[0])
	}
	if (newHotspot.timeStamps[newHotspot.timeStamps.length - 1] !== newEnd) {
		newHotspot.timeStamps.push(newEnd)
		newHotspot.points.push(newHotspot.points[newHotspot.points.length - 1])
	}

	return newHotspot
}

export function makeTimelineObjects(
	content: EditContent,
	setContent: React.Dispatch<React.SetStateAction<EditContent>>,
	setTime: (time: number) => void
): [TimelineCardProps[], number] {
	const normPolygons: TimelineCardProps[] = content.polygons.map((polygon, index) => {
		return {
			column: 0,
			start: polygon.start,
			end: polygon.end,
			name: polygon.title,
			type: 'polygon',
			color: polygon.color,
			isSelected: content.selectedMarkup?.type === 'polygon' && content.selectedMarkup?.index === index,
			onClick: () => {
				setContent(content => {
					return {
						...content,
						selectedMarkup: {
							type: 'polygon',
							index: index,
							subIndex: -1
						}
					}
				})
				setTime(polygon.start)
			},
			onDelete: () => {
				setContent(content => {
					return {
						...content,
						polygons: [...content.polygons.slice(0, index), ...content.polygons.slice(index + 1)] as EditPolygon[]
					}
				})
			},
			onDrag: (newStart: number, newEnd: number) => {
				setContent(content => {
					return {
						...content,
						polygons: [
							...content.polygons.slice(0, index) as EditPolygon[],
							applyDragToPolygon(content.polygons[index], newStart, newEnd) as EditPolygon,
							...content.polygons.slice(index + 1) as EditPolygon[]
						]
					}
				})
			},
			onDuplicate: () => {
				setContent(content => {
					const original = content.polygons[index]
					const cloned = { 
						...original,
						points: original.points.map(p => ({ ...p })),
						midPoints: original.midPoints.map(p => ({ ...p }))
					}
					// nudge slightly forward so it's visible
					cloned.start += 0.5
					cloned.end += 0.5
					return {
						...content,
						polygons: [...content.polygons, cloned]
					}
				})
			},
			onJump: (jumpTime: number) => {
				setTime(jumpTime)
			}
		}
	})
	const normHotspots: TimelineCardProps[] = content.hotSpots.map((hotspot, index) => {
		console.log(hotspot)
		return {
			column: 0,
			start: hotspot.timeStamps[0],
			end: hotspot.timeStamps[hotspot.timeStamps.length - 1],
			name: hotspot.title,
			type: 'hostpot',
			color: hotspot.color,
			isSelected: content.selectedMarkup?.type === 'hotspot' && content.selectedMarkup?.index === index,
			onClick: () => {
				setContent(content => {
					return {
						...content,
						selectedMarkup: {
							type: 'hotspot',
							index: index,
							subIndex: -1
						}
					}
				})
				setTime(hotspot.timeStamps[0])
			},
			onDelete: () => {
				setContent(content => {
					return {
						...content,
						hotSpots: [...content.hotSpots.slice(0, index), ...content.hotSpots.slice(index + 1)]
					}
				})
			},
			onDrag: (newStart: number, newEnd: number) => {
				setContent(content => {
					return {
						...content,
						hotSpots: [
							...content.hotSpots.slice(0, index),
							applyDragToHotspot(content.hotSpots[index], newStart, newEnd),
							...content.hotSpots.slice(index + 1)
						]
					}
				})
			},
			onDuplicate: () => {
				setContent(content => {
					const original = content.hotSpots[index]
					const cloned = { 
						...original,
						timeStamps: [...original.timeStamps],
						points: original.points.map(p => ({ ...p }))
					}
					// nudge slightly forward
					cloned.timeStamps = cloned.timeStamps.map(t => t + 0.5)
					return {
						...content,
						hotSpots: [...content.hotSpots, cloned]
					}
				})
			},
			onJump: (jumpTime: number) => {
				setTime(jumpTime)
			}
		}
	})
	const rawTimelineCards = normPolygons.concat(normHotspots)
	rawTimelineCards.sort((a, b) => a.start - b.start)


	let occupiedColums: (number | undefined)[] = []


	const finalCards: TimelineCardProps[] = rawTimelineCards.map((card) => {
		occupiedColums = occupiedColums.map(col => col && col < card.start ? undefined : col)
		for (let column = 0; column < occupiedColums.length; column++) {
			if (occupiedColums[column] === undefined) {
				occupiedColums[column] = card.end
				return {
					...card,
					column: column
				}
			}
		}
		occupiedColums.push(card.end)
		return {
			...card,
			column: occupiedColums.length - 1
		}
	})

	return [finalCards, occupiedColums.length]
}

export function applyMoveToHotspot(hotspot: EditHotSpot, delta: number): EditHotSpot {
		return {
				...hotspot, 
				timeStamps: hotspot.timeStamps.map(time => time + delta)
		}
}
